import { sleep } from "@/domains/event-loop/synchronization";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import type { AppStore } from "@/store/AppStore";
import { MemoryQSyncOperation } from "@/store/note";
import {
  CACHE_KEY_DIVIDER,
  type QOperation,
  type StorageAction,
  computeRestorePlan,
} from "@/store/note/helpers/computeRestorePlan";
import {
  deserializeSyncOperation,
  serializeSyncOperation,
} from "@/store/sync/operations/helpers/common";
import { makeAutoObservable, runInAction } from "mobx";

/**
 * NoteQueueObservable is a class that manages a queue of operations for a note.
 * It is used to store operations in localStorage per tab and restores them on page load.
 * It also periodically checks for orphaned operations (from crashed tabs) and restores them.
 *
 * Algorithm:
 * 1. On initialization:
 *    - Generates unique tabId using UUID
 *    - Restores any operations from localStorage for this or other tabs
 *    - Starts periodic check for orphaned operations
 *
 * 2. Operation Storage:
 *    - Stores only content-mutating note operations from the memory queue
 *    - Each operation is stored in localStorage with key: note_queue_cache-{noteId}-{tabId}
 *    - Operations are serialized before storage and deserialized on retrieval
 *
 * 3. Operation Processing:
 *    - Operations are processed sequentially from the queue
 *    - Each operation is executed and then removed from localStorage (if stored)
 *    - Deduplication happens based on uniqueId if specified
 *
 * 4. Orphaned Operation Recovery:
 *    - Every 1 second, checks localStorage for operations from other tabs
 *    - If operations found from inactive tabs, restores and processes them
 *    - Helps prevent lost operations if a tab crashes
 *
 * 5. Cleanup:
 *    - When component becomes unobserved, stops periodic checks
 *    - Clears operations from localStorage when processed
 *
 * Case 1:
 * - tabA has operations [op1, op2]
 * - tabA crashes
 * - new tabB starts
 * - tabB restores op1, op2 on construction, process them as normal
 *
 * Case 2:
 * - tabA has operations [op1, op2]
 * - tabB has operations [op3, op4]
 * - tabA crashes
 * - tabB process op3, op4 as normal
 * - tabA recovers and restores op1, op2 based on periodic check
 * - tabB process op1, op2 as normal
 *
 * Case 3:
 * - tabA has operations [op1, op2]
 * - tabB has operations [op3, op4]
 * - tabA crashes
 * - tabB crashes
 * - new tabC starts
 * - tabC restores op1, op2, op3, op4 on construction, process them as normal
 *
 * Note: we don't need to dedup operations as they are deduped on the sync Q level.
 * Note: localStorage is limited to 5MB, so we need to be mindful of the size of the operations we store.
 * Ideally, ops in localStorage are short lived, but in extreme cases, when sync Q is blocked for a very long time,
 * the size of the ops in localStorage can potentially outnumber the quota.
 * Note: performance considerations: it takes ~10ms to write 4.5mb of data to localStorage on modern Mac (~40ms when CPU is throttled 4x).
 *
 * TODO: extend this approach to also store template updates: UPDATE_TEMPLATE
 */
export class NoteQueueObservable {
  public static readonly CACHE_KEY = "note_queue_cache";
  public static readonly CHECK_INTERVAL = 1000; // 1 second
  public static readonly RETRY_DELAY = 500; // 0.5 second
  public readonly tabId = uuidModule.generate();

  private id: string;
  private checkInterval: number | null = null;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private isDisposed = false;

  // Preserve the operation ids that we processed to avoid ping-pong between tabs and allow deduping.
  private processedOperationIds: string[] = [];

  constructor(
    private store: AppStore,
    id: string
  ) {
    this.id = id;
    makeAutoObservable(this);

    this.restoreOperations();
    this.startPeriodicCheck();
  }

  // QUEUE FOR OPERATIONS
  operations: QOperation[] = [];

  isProcessing = false;

  // To separate diff note queues.
  get cacheUniqueKey() {
    return `${NoteQueueObservable.CACHE_KEY}-${this.id}`;
  }

  get isEmpty() {
    return !this.isProcessing && this.operations.length === 0;
  }

  push(operation: MemoryQSyncOperation, opt?: { dropUniqueId?: string; uniqueId?: string }) {
    if (this.isDisposed) return;

    const dropUniqueId = opt?.dropUniqueId || opt?.uniqueId;
    // Dedup by uniqueId.
    this.operations = this.operations.filter(
      (operation, i) =>
        // The head is prob. already being processed.
        i === 0 ||
        // Only some operations want to be deduped, e.g, metadata.
        !dropUniqueId ||
        // Keep other operations.
        operation.uniqueId !== dropUniqueId
    );
    this.operations.push({ operation, uniqueId: opt?.uniqueId });
    this.storeOperations();
    if (this.operations.length > 0) this.process();
  }

  async process() {
    if (this.isDisposed) return;
    if (this.isProcessing) return;
    runInAction(() => (this.isProcessing = true));

    try {
      while (this.operations.length > 0) {
        const { operation } = this.operations[0];
        if (!operation) {
          runInAction(() => {
            this.operations.shift();
            this.storeOperations();
          });
          continue;
        }

        try {
          await operation.execute();
        } catch (unknownError) {
          const error = unknownError as Error;
          logger.error({
            message: `[${this.id}] [debug] NoteQueueObservable: Error processing operation`,
            info: {
              operationId: operation.id,
              operationKind: operation.operationKind,
              error: objectModule.safeErrorAsJson(error),
            },
          });

          // Keep the failed operation at the head and retry later.
          this.scheduleRetry();
          return;
        }

        // Only remove it from the queue after it's processed.
        runInAction(() => {
          this.operations.shift();
          this.storeOperations();
          this.processedOperationIds.push(operation.id);
        });
      }
    } finally {
      runInAction(() => (this.isProcessing = false));
    }
  }

  async waitForQueue() {
    let done = false;
    setTimeout(() => {
      if (done || !this.operations.length) return;

      logger.warn({
        message: `[${this.id}] MemCommonEditorStore: Operation queue is taking too long`,
        info: {
          queueLength: this.operations.length,
          isProcessing: this.isProcessing,
          head: {
            id: this.operations[0].operation.id,
            kind: this.operations[0].operation.operationKind,
          },
        },
      });
    }, 800);

    while (this.isProcessing || this.operations.length > 0) {
      await sleep(100);
    }
    done = true;
  }

  /**
   * Restores cached operations across tabs.
   *
   * Reads all localStorage keys for this note, computes a restore plan (which ops to
   * restore, which keys to clean up), then executes the plan's side effects.
   */
  restoreOperations() {
    if (this.isDisposed) return;
    if (this.isProcessing) return;

    try {
      const entries = Object.keys(localStorage)
        .filter(key => key.startsWith(this.cacheUniqueKey))
        .flatMap(key => {
          const rawValue = localStorage.getItem(key);
          return rawValue ? [{ key, rawValue }] : [];
        });

      const plan = computeRestorePlan({
        entries,
        currentTabKey: `${this.cacheUniqueKey}-${this.tabId}`,
        inMemoryOperationIds: new Set(this.operations.map(op => op.operation.id)),
        processedOperationIds: new Set(this.processedOperationIds),
        deserialize: str => this.deserialize(this.store, str),
        shouldPersistOperation: op => this.shouldPersistOperation(op),
      });

      for (const err of plan.deserializeErrors) {
        logger.error({
          message: `[${this.id}] [debug] NoteQueueObservable: Error deserializing operation`,
          info: { key: err.key, error: objectModule.safeErrorAsJson(err.error) },
        });
      }

      this.executeStorageActions(plan.storageActions, "immediate");

      let persisted = true;
      if (plan.needsPersist) {
        runInAction(() => {
          if (plan.operationsToRestore.length > 0) {
            this.operations = [...this.operations, ...plan.operationsToRestore];
          }
          persisted = this.storeOperations();
        });
      }

      if (persisted) {
        this.executeStorageActions(plan.storageActions, "after-persist");
      }

      if (plan.operationsToRestore.length > 0) {
        this.process();
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: `[${this.id}] [debug] NoteQueueObservable: Error restoring operations`,
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    }
  }

  dispose() {
    this.isDisposed = true;
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  /**
   * Persists content-mutating operations that entered this note memory queue.
   *
   * We only persist operations that can update the note content document and carry non-empty
   * content payloads. This keeps the cache focused on updates that are expensive to lose while
   * still avoiding unrelated queue noise.
   */
  storeOperations(): boolean {
    try {
      const serialized = this.operations
        .filter(qOp => this.shouldPersistOperation(qOp.operation))
        .map(qOp => this.serialize(qOp));

      const key = `${this.cacheUniqueKey}-${this.tabId}`;

      if (serialized.length > 0) {
        localStorage.setItem(key, serialized.join(CACHE_KEY_DIVIDER));
        return true;
      }

      localStorage.removeItem(key);
      return true;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: `[${this.id}] [debug] NoteQueueObservable: Error storing operations`,
        info: {
          error: objectModule.safeErrorAsJson(error),
        },
      });
      return false;
    }
  }

  private startPeriodicCheck() {
    this.checkInterval = window.setInterval(() => {
      this.restoreOperations();
    }, NoteQueueObservable.CHECK_INTERVAL);
  }

  private scheduleRetry() {
    if (this.isDisposed) return;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      void this.process();
    }, NoteQueueObservable.RETRY_DELAY);
  }

  private executeStorageActions(actions: StorageAction[], timing: "immediate" | "after-persist") {
    for (const action of actions) {
      if (action.timing !== timing) continue;
      if (action.type === "remove") {
        localStorage.removeItem(action.key);
      } else {
        localStorage.setItem(action.key, action.value);
      }
    }
  }

  private serialize(op: QOperation): string {
    const { operation, uniqueId } = op;
    const obj = {
      uniqueId,
      ...serializeSyncOperation(operation),
    };

    return JSON.stringify(obj);
  }

  private deserialize(store: AppStore, str: string): QOperation {
    const obj = JSON.parse(str);
    if (!obj) {
      throw new Error("Could not parse operation");
    }

    const operation = deserializeSyncOperation(store, obj);
    if (!operation) {
      throw new Error("Failed to deserialize operation", obj);
    }

    return {
      operation,
      uniqueId: obj.uniqueId,
    };
  }

  private shouldPersistOperation(operation: MemoryQSyncOperation): boolean {
    if (operation.operationKind === "UPDATE_NOTE_CONTENT_USING_DIFF") {
      return Boolean((operation.payload as { encoded_content_diff?: string }).encoded_content_diff);
    }

    if (operation.operationKind === "UPDATE_NOTE_CONTENT_USING_TEMPLATE_AND_DIFF") {
      return Boolean((operation.payload as { encoded_content_diff?: string }).encoded_content_diff);
    }

    if (operation.operationKind === "UPDATE_NOTE_CONTENT_OVERWRITING_FULL_STATE_DESTRUCTIVELY") {
      return Boolean((operation.payload as { encoded_content?: string }).encoded_content);
    }

    return false;
  }
}
